// What a generated report contains, and in whose conventions.
//
// The console has ONE reader and can ask them what they want. A report has a
// reader it will never meet: it is emailed to a client, appended to a survey
// deliverable, filed against a contract. So the choices that are settings on
// screen become choices at generation time here — and, having been chosen, are
// named on the face of the document rather than left to be inferred.
//
// WHY UNITS WRITE THE CONSOLE'S OWN PREFERENCE. Picking Metric or Imperial here
// calls setUnitSystem(), the same preference the rest of the console reads. It
// would have been easy to give the report a private unit setting instead, and
// that is exactly the bug: the conditions grid, the meteograms and the panels
// all format through ui/units.ts, so a private setting would have printed a
// document whose charts disagreed with its tables. One preference, one answer.
// The "both" modes are additive — they name a primary AND print the other in
// brackets, so the grid and the charts still have an unambiguous system.
//
// SVP IS OFF BY DEFAULT and that is not timidity. The profile is a modelled
// first guess at the water column; it belongs in a report when somebody is
// planning a cast or an MBES run, and it is noise in a report about whether a
// deck crew can work on Thursday. Off is the right default for the common case.

import {
  getUnitSystem,
  setUnitSystem,
  fmtLenIn,
  fmtLimitLenIn,
  fmtDepthIn,
  type UnitSystem,
} from "../ui/units.js";
import { getPref, setPref } from "../ui/prefs.js";
import type { ReportClock } from "./report.js";

/**
 * The unit convention a report prints in.
 *
 * Four values rather than three, because "both" needs a primary: the meteogram
 * has one y-axis and a grid cell has room for one number. Making the primary
 * explicit in the choice beats inheriting it from wherever the console's toggle
 * happened to be left.
 */
export type ReportUnits = "metric" | "imperial" | "both-metric" | "both-imperial";

export interface ReportPrefs {
  clock: ReportClock;
  units: ReportUnits;
  /** Include the modelled sound-velocity profile. */
  svp: boolean;
}

const CLOCK_KEY = "report.clock";
const UNITS_KEY = "report.units";
const SVP_KEY = "report.svp";

const CLOCKS: ReportClock[] = ["site", "utc", "both"];
const UNITS: ReportUnits[] = ["metric", "imperial", "both-metric", "both-imperial"];

/** The stored options, defaulted. Anything unrecognised falls back rather than throws. */
export function loadReportPrefs(): ReportPrefs {
  const c = getPref(CLOCK_KEY);
  const u = getPref(UNITS_KEY);
  return {
    clock: CLOCKS.find((v) => v === c) ?? "both",
    // No stored choice means "whatever the console is set to" — the report then
    // matches the screen it was generated from, which is the least surprising
    // thing it can do on a first run.
    units: UNITS.find((v) => v === u) ?? getUnitSystem(),
    svp: getPref(SVP_KEY) === true,
  };
}

/**
 * Apply and persist. The unit half writes the CONSOLE's preference, so the
 * charts and the conditions grid — which format through ui/units.ts and know
 * nothing about this module — come out in the system the report asked for.
 */
export function applyReportPrefs(p: ReportPrefs): void {
  setPref(CLOCK_KEY, p.clock);
  setPref(UNITS_KEY, p.units);
  setPref(SVP_KEY, p.svp);
  setUnitSystem(primaryOf(p.units));
}

/** The system the charts, the grid and the leading number use. */
export function primaryOf(u: ReportUnits): UnitSystem {
  return u === "imperial" || u === "both-imperial" ? "imperial" : "metric";
}

/** The system printed in brackets, or null when only one is printed. */
export function secondaryOf(u: ReportUnits): UnitSystem | null {
  if (u === "both-metric") return "imperial";
  if (u === "both-imperial") return "metric";
  return null;
}

/**
 * A length in the report's convention: "2.1 m", "6.9 ft", or "2.1 m (6.9 ft)".
 *
 * The three length formatters are kept apart rather than collapsed into one,
 * because they round differently on purpose — see ui/units.ts. A limit shown to
 * the nearest foot would state a threshold the operator never set.
 */
export function repLen(m: number, u: ReportUnits): string {
  return dual(m, u, fmtLenIn);
}
export function repLimitLen(m: number, u: ReportUnits): string {
  return dual(m, u, fmtLimitLenIn);
}
export function repDepth(m: number, u: ReportUnits): string {
  return dual(m, u, fmtDepthIn);
}

function dual(m: number, u: ReportUnits, f: (m: number, s: UnitSystem) => string): string {
  const lead = f(m, primaryOf(u));
  const other = secondaryOf(u);
  return other === null ? lead : `${lead} (${f(m, other)})`;
}

/** How the document names its unit convention, on its face. */
export function unitsNote(u: ReportUnits): string {
  const other = secondaryOf(u);
  const leadWord = primaryOf(u) === "imperial" ? "feet" : "metres";
  if (other === null) {
    return `Heights and depths are in ${leadWord}.`;
  }
  const otherWord = other === "imperial" ? "feet" : "metres";
  return (
    `Heights and depths are in ${leadWord}, with ${otherWord} in brackets. ` +
    `Charts and the conditions grid use ${leadWord} only — a plot has one axis.`
  );
}

/**
 * What never converts, said once so the reader does not have to wonder.
 *
 * Wind in knots, visibility and ranges in nautical miles, and wave period in
 * seconds are the marine convention in BOTH systems; the sound-velocity profile
 * is SI end to end because its depths pair with metres-per-second and with the
 * SI file the survey software ingests. None of these move with the toggle, and a
 * reader who spots feet elsewhere is entitled to be told why.
 */
export const UNITS_INVARIANT_NOTE =
  "Wind is in knots, distances in nautical miles and wave period in seconds " +
  "regardless of the setting above — the marine convention in both systems. The " +
  "sound-velocity profile is metric throughout: its depths pair with m/s.";
